import type { ChecklistItem } from '../types/schema'
import type { WorkspaceRole } from '../types/workspace'
import { roleCanPerform } from './workspacePermissions'

export type ChecklistBaseOperationalPermissions = {
  assign: boolean
  status: boolean
  proof: boolean
  memo: boolean
  comment: boolean
  locationCheckin: boolean
}

export type ChecklistOperationalPermissions = ChecklistBaseOperationalPermissions & {
  complete: boolean
}

export type ChecklistOperationalAccess = {
  permissions: ChecklistOperationalPermissions
  hasAssignee: boolean
  isCurrentUserAssignee: boolean
  isAdmin: boolean
  isHelper: boolean
  workDisabledReason: string | null
  completionDisabledReason: string | null
}

export const CHECKLIST_UNASSIGNED_WORK_REASON = 'Assign this item before work actions.'
export const CHECKLIST_HELPER_ASSIGNMENT_REASON = 'Helpers can work only their assigned visible items.'
export const CHECKLIST_COMPLETION_OWNER_REASON = 'Only the assignee or an admin can complete this item.'

type ChecklistOperationalAccessInput = {
  item: Pick<ChecklistItem, 'assigneeId' | 'isHelperVisible'>
  currentUserId?: string | null
  activeRole?: WorkspaceRole | null
  isReadOnly?: boolean
  basePermissions: ChecklistBaseOperationalPermissions
}

export const getChecklistOperationalAccess = ({
  item,
  currentUserId,
  activeRole,
  isReadOnly = false,
  basePermissions,
}: ChecklistOperationalAccessInput): ChecklistOperationalAccess => {
  const hasAssignee = typeof item.assigneeId === 'string' && item.assigneeId.trim().length > 0
  const isCurrentUserAssignee = Boolean(currentUserId && hasAssignee && item.assigneeId === currentUserId)
  const isAdmin = Boolean(activeRole && roleCanPerform(activeRole, 'ADMIN'))
  const isHelper = activeRole === 'HELPER'
  const helperCanWork = isHelper && isCurrentUserAssignee && item.isHelperVisible !== false
  const memberCanWork = !isHelper && hasAssignee
  const canUseWorkSurface = !isReadOnly && hasAssignee && (helperCanWork || memberCanWork)
  const statusAllowed = isHelper ? helperCanWork : basePermissions.status
  const proofAllowed = isHelper ? helperCanWork : basePermissions.proof
  const memoAllowed = isHelper ? helperCanWork : basePermissions.memo
  const commentAllowed = isHelper ? helperCanWork : basePermissions.comment
  const locationAllowed = isHelper ? helperCanWork : basePermissions.locationCheckin
  const workDisabledReason = !hasAssignee
    ? CHECKLIST_UNASSIGNED_WORK_REASON
    : isHelper && !helperCanWork
      ? CHECKLIST_HELPER_ASSIGNMENT_REASON
      : null
  const completeAllowedByOwnership = isAdmin || isCurrentUserAssignee

  return {
    permissions: {
      assign: !isReadOnly && !isHelper && basePermissions.assign,
      status: canUseWorkSurface && statusAllowed,
      proof: canUseWorkSurface && proofAllowed,
      memo: canUseWorkSurface && memoAllowed,
      comment: canUseWorkSurface && commentAllowed,
      locationCheckin: canUseWorkSurface && locationAllowed && completeAllowedByOwnership,
      complete: canUseWorkSurface && statusAllowed && completeAllowedByOwnership,
    },
    hasAssignee,
    isCurrentUserAssignee,
    isAdmin,
    isHelper,
    workDisabledReason,
    completionDisabledReason: hasAssignee && !completeAllowedByOwnership ? CHECKLIST_COMPLETION_OWNER_REASON : null,
  }
}
